import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import {
  faSpinner,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faCalendarAlt,
  faIdCard,
  faFileText,
  faUser,
  faBell
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { PushNotification, PushNotificationID } from '@/models';
import { PushNotificationStatus } from '@/models';

export default function AdminPushNotificationDetail() {
  const { notificationId } = useParams<{ notificationId: string }>();
  const [notification, setNotification] = useState<PushNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);

  useEffect(() => {
    if (notificationId) {
      fetchNotificationDetails();
    }
  }, [notificationId]);

  const fetchNotificationDetails = async () => {
    if (!notificationId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const response = await APIRoutes.getPushNotification(notificationId as PushNotificationID);
      setNotification(response);
    } catch (err) {
      console.error('Critical error loading push notification details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Push-Benachrichtigung-Details');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: PushNotificationStatus) => {
    switch (status) {
      case 'WAITING':
        return <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-yellow-600" />;
      case 'SUCCESS':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-600" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: PushNotificationStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  // List of triggering-entity references (only the ones that are set)
  const references: { label: string; value: string }[] = [];
  if (notification !== null) {
    if (notification.coreRecommendationID !== null) references.push({ label: 'Kernstrategie-Empfehlung', value: notification.coreRecommendationID });
    if (notification.earningsRecommendationID !== null) references.push({ label: 'Earnings-Empfehlung', value: notification.earningsRecommendationID });
    if (notification.swingTradeRecommendationID !== null) references.push({ label: 'Swing-Trade-Empfehlung', value: notification.swingTradeRecommendationID });
    if (notification.longTermRecommendationID !== null) references.push({ label: 'Langzeit-Empfehlung', value: notification.longTermRecommendationID });
    if (notification.videoID !== null) references.push({ label: 'Video', value: notification.videoID });
    if (notification.eventID !== null) references.push({ label: 'Event', value: notification.eventID });
    if (notification.subscriptionID !== null) references.push({ label: 'Abonnement', value: notification.subscriptionID });
  }

  const extraDataEntries = notification !== null ? Object.entries(notification.extraData ?? {}) : [];

  if (criticalError) {
    return (
      <FullscreenError
        title="Push-Benachrichtigung nicht verfügbar"
        message={error || 'Die Push-Benachrichtigung konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchNotificationDetails}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/push-notifications"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Push-Benachrichtigung Details
            </h1>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {notification && !loading && (
          <div className="space-y-6">
            {/* Status & Basic Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Status & Grundinformationen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-3 py-2 text-sm font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                    {getStatusIcon(notification.status)}
                    <span className="ml-2">{notification.status}</span>
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-1" />
                    Benachrichtigungs-ID
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {notification.id}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-1" />
                    Benutzer
                  </label>
                  <Link
                    to={`/admin/users/${notification.userID}`}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded block"
                  >
                    {notification.userID}
                  </Link>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-1" />
                    Firebase Message-ID
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {notification.firebaseMessageID ?? '–'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Erstellt
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {formatDate(notification.created)}
                  </div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                <FontAwesomeIcon icon={faFileText} className="w-5 h-5 mr-2" />
                Titel
              </h2>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="text-sm text-gray-900 dark:text-white">
                  {notification.title}
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                <FontAwesomeIcon icon={faFileText} className="w-5 h-5 mr-2" />
                Inhalt
              </h2>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
                  {notification.text}
                </pre>
              </div>
            </div>

            {/* Image URL */}
            {notification.imageURL !== null && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  <FontAwesomeIcon icon={faBell} className="w-5 h-5 mr-2" />
                  Bild-URL
                </h2>
                <a
                  href={notification.imageURL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                >
                  {notification.imageURL}
                </a>
              </div>
            )}

            {/* Extra Data */}
            {extraDataEntries.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Zusatzdaten
                </h2>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-1">
                  {extraDataEntries.map(([key, value]) => (
                    <div key={key} className="text-sm font-mono text-gray-900 dark:text-white">
                      <span className="text-gray-500 dark:text-gray-400">{key}:</span> {value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delivery / Retry Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Versandinformationen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-1" />
                    Versuche
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.tryCount} / {notification.maxTries}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Nächster Versuch
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.nextTryTime !== null ? formatDate(notification.nextTryTime) : '–'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Versendet am
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.sentTime !== null ? formatDate(notification.sentTime) : 'Noch nicht versendet'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Fehlgeschlagen am
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.failTime !== null ? formatDate(notification.failTime) : '–'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Geöffnet am
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.opened !== null ? formatDate(notification.opened) : 'Noch nicht geöffnet'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Zuletzt aktualisiert
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {notification.updated !== null ? formatDate(notification.updated) : 'Nie aktualisiert'}
                  </div>
                </div>
              </div>
            </div>

            {/* Triggering Entity References */}
            {references.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Auslösende Entität
                </h2>
                <div className="space-y-3">
                  {references.map((ref) => (
                    <div key={ref.label}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {ref.label}
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        {ref.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Information */}
            {notification.failReason !== null && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 mr-2 text-red-600" />
                  Fehlerinformationen
                </h2>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <pre className="text-sm text-red-700 dark:text-red-400 whitespace-pre-wrap font-mono">
                    {notification.failReason}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
